import React from 'react'
/**
 * A self-contained image blurring component.
 * It uses a canvas to allow an admin to draw rectangles over an image and apply a blur effect.
 *
 * @param {string} imageUrl - The source URL of the image to edit.
 * @param {function(string, string): Promise<void>} onSave - Async function to call when saving. It receives the base64 data and original image URL.
 * @param {function} onClose - Function to call when the editor is closed.
 */
export default function ImageBlurEditor({ imageUrl, onSave, onClose }) {
    const canvasRef = React.useRef(null);
    const [image, setImage] = React.useState(null);
    const [blurRegions, setBlurRegions] = React.useState([]);
    const [history, setHistory] = React.useState([[]]); // History of blurRegions states
    const [historyIndex, setHistoryIndex] = React.useState(0);
    const [isDrawing, setIsDrawing] = React.useState(false);
    const [startPoint, setStartPoint] = React.useState({ x: 0, y: 0 });
    const [currentRect, setCurrentRect] = React.useState(null);
    const [blurStrength, setBlurStrength] = React.useState(10); // Default blur strength in pixels
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState(null);

    // --- Canvas & Image Drawing ---

    const redrawCanvas = React.useCallback((skipSelectionRect = false) => {
        const canvas = canvasRef.current;
        if (!canvas || !image) return;
        const ctx = canvas.getContext('2d');

        // Clear canvas and draw the original image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        // Apply blur to each region
        blurRegions.forEach(region => {
            ctx.save();
            ctx.filter = `blur(${region.blur}px)`;
            // Draw the blurred portion of the image onto itself
            ctx.drawImage(canvas, region.x, region.y, region.width, region.height, region.x, region.y, region.width, region.height);
            ctx.restore();
        });

        // Draw the temporary selection rectangle while dragging (only if not skipped)
        if (!skipSelectionRect && isDrawing && currentRect) {
            ctx.strokeStyle = 'rgba(0, 123, 255, 0.7)';
            ctx.lineWidth = 2;
            ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
        }
    }, [image, blurRegions, isDrawing, currentRect]);

    // Fast redraw for selection rectangle only
    const drawSelectionRect = React.useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !currentRect) return;
        const ctx = canvas.getContext('2d');
        
        // Only redraw the selection rectangle area
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 123, 255, 0.7)';
        ctx.lineWidth = 2;
        ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
        ctx.restore();
    }, [currentRect]);

    // Load the image and set up the canvas
    React.useEffect(() => {
        const img = new Image();
        img.crossOrigin = "Anonymous"; // Important for loading images from a different origin (even same domain)
        img.src = imageUrl;
        img.onload = () => {
            const canvas = canvasRef.current;
            if (canvas) {
                // Scale canvas to image dimensions
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                setImage(img);
            }
        };
        img.onerror = () => {
            setError("Failed to load the image. It might be a cross-origin issue.");
        };
    }, [imageUrl]);

    // Redraw canvas whenever the image or blur regions change
    React.useEffect(() => {
        redrawCanvas();
    }, [image, blurRegions, redrawCanvas]);

    // --- History Management (Undo/Redo) ---

    const updateHistory = (newRegions) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newRegions);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };

    const handleUndo = () => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            setBlurRegions(history[newIndex]);
        }
    };

    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            setBlurRegions(history[newIndex]);
        }
    };

    // --- Mouse Event Handlers for Drawing ---

    const getCanvasCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    // --- Touch Event Handlers for Mobile ---

    const handleTouchStart = (e) => {
        // Prevent default touch actions like scrolling the page
        e.preventDefault();
        if (e.touches.length > 0) {
            // Pass the first touch point to the existing mouse down handler
            handleMouseDown(e.touches[0]);
        }
    };

    const handleTouchMove = (e) => {
        e.preventDefault();
        if (e.touches.length > 0) {
            handleMouseMove(e.touches[0]);
        }
    };

    const handleTouchEnd = (e) => {
        e.preventDefault();
        handleMouseUp(); // The event object is not used in handleMouseUp
    };

    const handleMouseDown = (e) => {
        e.preventDefault(); // Prevent default behavior
        e.stopPropagation(); // Prevent event bubbling to parent
        setIsDrawing(true);
        setStartPoint(getCanvasCoordinates(e));
        setCurrentRect(null);
    };

    const handleMouseMove = (e) => {
        if (!isDrawing) return;
        e.preventDefault(); // Prevent default behavior
        e.stopPropagation(); // Prevent event bubbling to parent
        const endPoint = getCanvasCoordinates(e);
        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(startPoint.x - endPoint.x);
        const height = Math.abs(startPoint.y - endPoint.y);
        setCurrentRect({ x, y, width, height });
        
        // Use optimized drawing for mouse move - only redraw the selection rectangle
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            // Redraw the canvas without selection rectangle
            redrawCanvas(true);
            // Then draw just the selection rectangle
            drawSelectionRect();
        }
    };

    const handleMouseUp = (e) => {
        e.preventDefault(); // Prevent default behavior
        e.stopPropagation(); // Prevent event bubbling to parent
        if (!isDrawing || !currentRect || currentRect.width < 5 || currentRect.height < 5) {
            setIsDrawing(false);
            setCurrentRect(null);
            redrawCanvas();
            return;
        }
        const newRegion = { ...currentRect, blur: blurStrength };
        const newRegions = [...blurRegions, newRegion];
        setBlurRegions(newRegions);
        updateHistory(newRegions);
        setIsDrawing(false);
        setCurrentRect(null);
    };

    // --- Action Handlers ---

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dataUrl = canvas.toDataURL('image/png');
        try {
            await onSave(dataUrl, imageUrl);
            setIsSaving(false);
        } catch (err) {
            setError(err.message || "An unknown error occurred during save.");
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[102] p-4" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Never close on backdrop click - user must use X or Cancel button
        }}>
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[95vh] flex flex-col" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Never close on content click
            }}>
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-900">Image Blur Editor</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 disabled:opacity-50" disabled={isSaving}>
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-grow flex flex-col md:flex-row overflow-hidden">
                    {/* Controls */}
                    <div className="w-full md:w-64 p-4 border-b md:border-b-0 md:border-r bg-gray-50 space-y-6">
                        <div>
                            <label htmlFor="blur-strength" className="block text-sm font-medium text-gray-700">Blur Strength: {blurStrength}px</label>
                            <input
                                id="blur-strength"
                                type="range"
                                min="1"
                                max="50"
                                value={blurStrength}
                                onChange={(e) => setBlurStrength(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                        <div className="flex space-x-2">
                            <button onClick={handleUndo} disabled={historyIndex === 0 || isSaving} className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50">
                                <i className="fas fa-undo mr-2"></i>Undo
                            </button>
                            <button onClick={handleRedo} disabled={historyIndex === history.length - 1 || isSaving} className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50">
                                <i className="fas fa-redo mr-2"></i>Redo
                            </button>
                        </div>
                        <p className="text-xs text-gray-500">Click and drag on the image to select an area to blur.</p>
                        {error && <div className="text-red-500 text-sm p-2 bg-red-100 rounded">{error}</div>}
                    </div>

                    {/* Canvas */}
                    <div className="flex-grow p-4 flex items-center justify-center bg-gray-200 overflow-auto">
                        <canvas
                            ref={canvasRef}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp} // End drawing if mouse leaves canvas for desktop
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onTouchCancel={handleTouchEnd} // Also handle if the touch is interrupted
                            className="max-w-full max-h-full object-contain cursor-crosshair"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t flex justify-end space-x-4">
                    <button onClick={onClose} disabled={isSaving} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50">
                        Cancel
                    </button>
                    <button onClick={handleSave} disabled={isSaving} className="primary-gradient text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50">
                        {isSaving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : 'Save Image'}
                    </button>
                </div>
            </div>
        </div>
    );
}
